import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Lock } from 'lucide-react';
import ContextualHelp from '@/components/ContextualHelp';
import VoiceInputButton from '@/components/VoiceInputButton';
import PageHero from '@/components/PageHero';

const JOURNAL_HELP = {
  whatIs: "Your private reflection space. Freeform entries + AI reflections that surface patterns you'd miss on your own.",
  whyMatters: "Writing turns fog into shape. Even three lines a day builds the self-awareness that changes how you show up in every other part of your life.",
  howToUse: "Write honestly, not perfectly. Use a prompt if you're stuck. Ask for an AI reflection when you want a second lens on what you wrote.",
  tips: [
    "There's no such thing as a perfect entry. Honest > polished.",
    "Recurring themes usually point to what deserves your energy.",
    "Your journal is private. Nothing here is shared with anyone unless you export it yourself.",
  ],
};

export default function Journal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const locked = !!user?.trial?.locked;
  const [prompts, setPrompts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [reflections, setReflections] = useState({});
  const [reflecting, setReflecting] = useState(null);

  const load = () => http.get('/journal').then(r => setEntries(r.data));
  useEffect(() => { http.get('/journal/prompts').then(r => setPrompts(r.data)); load(); }, []);

  const save = async () => {
    if (!text.trim()) return;
    if (locked) { toast.error('Saving your journal is a member feature — upgrade to keep your entries.'); return; }
    setSaving(true);
    try { await http.post('/journal', { prompt: prompt || null, entry: text.trim() });
      setText(''); setPrompt(''); toast.success('Entry saved'); load();
    } catch { toast.error('Could not save'); }
    setSaving(false);
  };

  const reflect = async (entry) => {
    if (!user?.is_premium) { toast.error('AI reflections are included with Brotherhood+ — subscribe to unlock.'); return; }
    setReflecting(entry.id);
    try {
      const { data } = await http.post('/journal/reflect', { entry: entry.entry });
      setReflections(r => ({ ...r, [entry.id]: data.reflection }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reflect'); }
    setReflecting(null);
  };

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6 lg:space-y-8" data-testid="journal-page">
      <PageHero photo="journal" alt="A Black man reflecting quietly" />
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Journal</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1 lg:mt-2 flex items-start gap-2 flex-wrap">Get it out of your head.<ContextualHelp pageTitle="Journal" content={JOURNAL_HELP} testid="journal-help"/></h1>
        <p className="hidden lg:block text-navy/60 mt-3 max-w-2xl">Private space to think out loud. AI reflections are included with your Brotherhood+ membership.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
        <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft space-y-3 lg:col-span-3 lg:sticky lg:top-24 lg:self-start">
          <p className="text-xs uppercase tracking-widest text-navy/50">Choose a prompt (optional)</p>
          <div className="flex flex-wrap gap-2">
            {prompts.map(p => (
              <button key={p} onClick={()=>setPrompt(p)} data-testid={`prompt-${p.slice(0,20)}`}
                className={`text-xs px-3 py-2 rounded-full ${prompt===p ? 'bg-navy text-white' : 'bg-beige text-navy'}`}>
                {p}
              </button>
            ))}
          </div>
          {prompt && <p className="font-heading text-lg lg:text-xl pt-2">{prompt}</p>}
          <div className="relative">
            <textarea data-testid="journal-textarea" value={text} onChange={e=>setText(e.target.value)}
              rows={6} placeholder="Start writing…"
              className="w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl p-4 pr-14 outline-none focus:ring-2 focus:ring-gold lg:min-h-[220px]" />
            <div className="absolute right-3 bottom-3">
              <VoiceInputButton
                compact
                testid="journal-voice-btn"
                onTranscript={(chunk) => setText(prev => (prev + ' ' + chunk).trim())}
              />
            </div>
          </div>          {locked ? (
            <a href="/subscription/checkout" data-testid="journal-locked-save"
              className="flex items-center justify-center gap-2 w-full bg-gold text-navy rounded-full py-3 font-medium hover:bg-gold/90 transition-smooth">
              <Lock size={15}/> Upgrade to save your journal
            </a>
          ) : (
            <button data-testid="journal-save" onClick={save} disabled={saving || !text.trim()}
              className="w-full bg-navy text-white rounded-full py-3 font-medium hover:bg-navy-soft disabled:opacity-40">
              {saving ? 'Saving…' : 'Save entry'}
            </button>
          )}
          <p className="text-[10px] text-navy/40 text-center">Private. Admins never see this.</p>
        </div>

        <div className="space-y-3 lg:space-y-4 lg:col-span-2">
          {locked ? (
            <div className="bg-navy text-white rounded-3xl p-6 lg:p-7 text-center card-soft" data-testid="journal-history-locked">
              <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center mx-auto"><Lock size={20} className="text-gold"/></div>
              <h3 className="font-heading text-xl mt-3">Journal history is a member feature</h3>
              <p className="text-white/70 text-sm mt-2">Upgrade to save your entries, revisit them anytime, and unlock AI reflections.</p>
              <button onClick={() => nav('/subscription/checkout')} data-testid="journal-history-upgrade"
                className="mt-4 bg-gold text-navy rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-gold/90 transition-smooth">
                Upgrade to full access
              </button>
            </div>
          ) : (<>
          {entries.length > 0 && <p className="hidden lg:block text-xs uppercase tracking-widest text-navy/50 font-semibold">Past entries</p>}
          {entries.map(e => (
            <div key={e.id} data-testid={`entry-${e.id}`} className="bg-white rounded-3xl p-5 lg:p-6 card-soft">
              <p className="text-xs text-navy/50">{new Date(e.created_at).toLocaleString()}</p>
              {e.prompt && <p className="text-sm text-gold font-medium mt-1">{e.prompt}</p>}
              <p className="text-navy/80 whitespace-pre-wrap mt-2 text-sm">{e.entry}</p>
              {reflections[e.id] ? (
                <div className="mt-3 bg-beige rounded-2xl p-4" data-testid={`reflection-${e.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase tracking-widest text-gold font-semibold inline-flex items-center gap-1"><Sparkles size={10}/>AI Reflection</p>
                  </div>
                  <p className="text-sm text-navy/80 whitespace-pre-wrap">{reflections[e.id]}</p>
                </div>
              ) : (
                <button data-testid={`reflect-btn-${e.id}`} onClick={()=>reflect(e)} disabled={reflecting===e.id}
                  className="mt-3 text-xs inline-flex items-center gap-1 text-gold hover:text-gold-hover">
                  <Sparkles size={12}/>{reflecting===e.id ? 'Reflecting…' : `Get AI reflection${user?.is_premium ? '' : ' — Members only'}`}
                </button>
              )}
            </div>
          ))}
          </>)}
        </div>
      </div>
    </div>
  );
}
